import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Visually builds a CSS box-shadow effect with live preview controls for offset, blur, spread, color, and opacity, and generates the corresponding CSS code.`,
  how: `Adjust the sliders until the shadow preview looks right, then copy the generated CSS code into your project.`,
  tip: `Subtle shadows (low opacity, moderate blur) usually look more modern and professional than large, dark, sharp-edged ones — it's easy to overdo a shadow effect without realizing it until you compare it against a more restrained version.`,
};

// Defined outside the page component: declaring it inside re-created it on every render, which
// dropped the slider mid-drag.
const Slider = ({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
}) => (
  <div>
    <div className="flex justify-between mb-1">
      <Label className="text-sm" htmlFor={`bs-${label}`}>{label}</Label>
      <span className="text-sm text-muted-foreground">{value}</span>
    </div>
    <input
      id={`bs-${label}`}
      aria-label={label}
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="w-full"
    />
  </div>
);

export default function BoxShadowGenerator() {
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(4);
  const [blur, setBlur] = useState(12);
  const [spread, setSpread] = useState(0);
  const [color, setColor] = useState("#000000");
  const [opacity, setOpacity] = useState(20);
  const [inset, setInset] = useState(false);

  const rgba = () => {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
  };

  const css = `box-shadow: ${inset ? "inset " : ""}${offsetX}px ${offsetY}px ${blur}px ${spread}px ${rgba()};`;

  const copy = () => {
    void copyText(css, "Copied to clipboard");
  };

  // Was useLogToolUsage(inset, undefined, { ownActions: true }) -- a checkbox toggle state, not a
  // "has output" signal (css is always computed, never empty, so the
  // real signal is just true).
  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <Slider
            label="Offset X"
            value={offsetX}
            onChange={setOffsetX}
            min={-50}
            max={50}
          />
          <Slider
            label="Offset Y"
            value={offsetY}
            onChange={setOffsetY}
            min={-50}
            max={50}
          />
          <Slider
            label="Blur"
            value={blur}
            onChange={setBlur}
            min={0}
            max={100}
          />
          <Slider
            label="Spread"
            value={spread}
            onChange={setSpread}
            min={-50}
            max={50}
          />
          <Slider
            label="Opacity"
            value={opacity}
            onChange={setOpacity}
            min={0}
            max={100}
          />
          <div className="flex items-center gap-3">
            <Label className="text-sm">Color</Label>
            <input
              type="color"
              aria-label="Shadow colour"
              value={color}
              onChange={e => setColor(e.target.value)}
              className="h-9 w-16 rounded border"
            />
            <label className="flex items-center gap-2 text-sm ml-auto">
              <input
                type="checkbox"
                aria-label="Inset shadow"
                checked={inset}
                onChange={e => setInset(e.target.checked)}
              />
              Inset
            </label>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center gap-6">
          <div
            className="w-48 h-32 rounded-xl bg-card border"
            style={{
              boxShadow: `${inset ? "inset " : ""}${offsetX}px ${offsetY}px ${blur}px ${spread}px ${rgba()}`,
            }}
          />
        </div>
      </div>
      <div className="mt-6">
        <div className="rounded-lg border bg-muted/30 p-4 font-mono text-sm">
          {blocked ? "— blocked at today's usage limit —" : css}
        </div>
        <Button className="mt-3" onClick={copy} disabled={blocked}>
          Copy CSS
        </Button>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
